import { useEffect, useState, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  Provider,
  IllustratedMessage,
  Heading,
  Content,
  Text,
  ButtonGroup,
  Button,
} from "@react-spectrum/s2";
import { useNavigate } from "react-router";

import Rocket from "@react-spectrum/s2/illustrations/gradient/generic1/Rocket";

import {
  IMS,
  markNextImsSignInForceAccountPicker,
} from "@app/services/imsService";
import { store } from "../../store";

import App from "../App";
import LoadingSpinner from "./LoadingSpinner";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const IMS_CONFIG = {
  clientId: process.env.IMS_CLIENT_ID,
  scope: process.env.IMS_SCOPE,
  adobeIncOrgId: process.env.ADOBE_INC_ORG_ID,
  environment: process.env.IMS_ENV,
};

const BESTORY_URL = "https://bestory.adobe.com";

const parseAllowedEmails = () => {
  if (process.env.ALLOW_LIST) {
    return process.env.ALLOW_LIST.split(",")
      .map((email) => email.trim())
      .filter(Boolean);
  }
  return [];
};

let imsBootstrapStarted = false;

function AuthWrapper({ locale = "en-US" }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdobeIncMember, setIsAdobeIncMember] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [allowedEmails] = useState(parseAllowedEmails());

  const navigate = useNavigate();

  const initializeIMS = async () => {
    try {
      setError(null);
      setIsInitializing(true);

      await IMS.initialize(IMS_CONFIG);

      // Check authentication status
      await checkAuthStatus();
    } catch (err) {
      console.error("IMS initialization failed:", err);
      setError(err.message || "IMS initialization failed");
      setIsAuthenticated(false);
    } finally {
      setIsInitializing(false);
    }
  };

  /**
   * Check if email is in the allowed list
   */
  const isEmailAllowed = useCallback(
    (email) => {
      if (!email) return false;

      // Allow all users
      if (allowedEmails.length === 0) {
        return true;
      }

      const normalizedEmail = email.toLowerCase().trim();
      const isAllowed = allowedEmails.some(
        (allowedEmail) => allowedEmail.toLowerCase().trim() === normalizedEmail,
      );

      console.log("Is Allowed", isAllowed);

      return isAllowed;
    },
    [allowedEmails],
  );

  const checkUserAuthorization = useCallback((profile) => {
    const isAdobeInc = profile?.ownerOrg === IMS_CONFIG.adobeIncOrgId;

    console.log("Is Adobe Inc", isAdobeInc);

    return {
      isAdobeIncMember: isAdobeInc === true,
      isEmailAllowed: isEmailAllowed(profile?.email),
    };
  }, []);

  /**
   * Check authentication status
   */
  const checkAuthStatus = async () => {
    try {
      // Check if user is signed in
      const isSignedIn = IMS.isSignedIn;

      if (isSignedIn) {
        const profile = await IMS.getUserProfile();

        const { isAdobeIncMember, isEmailAllowed } =
          checkUserAuthorization(profile);

        setIsAuthenticated(true);
        setIsAdobeIncMember(isAdobeIncMember);
        setIsAuthorized(isEmailAllowed);

        const token = await IMS.getAccessToken();
        store.getState().setIms({
          ims: {
            profile: {
              id: profile.id,
              email: profile.email,
              fullName: profile.fullName,
              firstName: profile.firstName,
              lastName: profile.lastName,
              ownerOrg: profile.ownerOrg,
              ldap: profile.ldap,
              avatar: `https://ab.adobe-identity.com/api/avatars/50/${profile.id}`,
            },
            token: token ?? null,
            org: null,
            isLoggedIn: true,
          },
          locale: "en-US",
        });

        // Handle redirect if needed
        const redirectPath = store.getState().redirectPath;
        if (redirectPath) {
          store.getState().setRedirectPath(null);
          navigate(redirectPath);
        }
      } else {
        // Not authenticated, trigger sign in
        setIsAuthenticated(false);
        setIsAuthorized(false);
        await handleLogin();
      }
    } catch (err) {
      console.error("Authentication check failed:", err);
      const needsForcedPicker = err?.message?.startsWith("401:");
      const needsSignIn =
        needsForcedPicker || err?.message === "User is not signed in!";
      if (needsSignIn) {
        store.getState().clearIms();
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setError(null);
        if (needsForcedPicker) {
          markNextImsSignInForceAccountPicker();
        }
        await handleLogin();
        return;
      }
      setError(err.message || "Authentication failed");
      setIsAuthenticated(false);
      setIsAuthorized(false);
    }
  };

  /**
   * Handle login
   */
  const handleLogin = async () => {
    try {
      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      store.getState().setRedirectPath(currentPath);

      await IMS.signIn(false);
    } catch (err) {
      console.error("Login failed:", err);
      setError(err.message || "Login failed");
    }
  };

  /**
   * Sign out and force account picker
   */
  const handleSwitchAccount = async () => {
    markNextImsSignInForceAccountPicker();
    await handleLogout();
  };

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    try {
      await IMS.signOut();
      setIsAuthenticated(false);
      setIsAdobeIncMember(false);
      setIsAuthorized(false);
    } catch (err) {
      console.error("Logout failed:", err);
      // Even if logout fails, clear local state
      store.getState().clearIms();
      setIsAuthenticated(false);
      setIsAdobeIncMember(false);
      setIsAuthorized(false);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  useEffect(() => {
    if (imsBootstrapStarted) {
      return;
    }
    imsBootstrapStarted = true;
    initializeIMS();
  }, []);

  // Not on the allow list: redirect to the public BeStory app, keeping the
  // path/query/hash so it lands on the same resource.
  const shouldRedirectToBestory =
    !isInitializing && !error && isAuthenticated && !isAuthorized;

  useEffect(() => {
    if (shouldRedirectToBestory && window.location.origin !== BESTORY_URL) {
      const { pathname, search, hash } = window.location;
      window.location.replace(`${BESTORY_URL}${pathname}${search}${hash}`);
    }
  }, [shouldRedirectToBestory]);

  if (isInitializing) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Provider
        styles={style({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        })}
      >
        <IllustratedMessage size="L" styles={style({ maxWidth: 640 })}>
          <Rocket />
          <Heading>BeStory</Heading>
          <Content
            styles={style({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              // gap: 16,
            })}
          >
            <Text>Authentication Error</Text>
          </Content>
          <ButtonGroup>
            <Button onPress={handleRetry} variant="secondary">
              Retry
            </Button>
          </ButtonGroup>
        </IllustratedMessage>
      </Provider>
    );
  }

  // Not authenticated yet — IMS redirect should be in flight
  if (!isAuthenticated) {
    return <LoadingSpinner />;
  }

  // Authenticated but not on the allow list: the effect above hands off to the
  // public BeStory app.
  if (isAuthenticated && !isAuthorized) {
    return <LoadingSpinner />;
  }

  // Show error state if user is authenticated but not Adobe Inc member
  if (isAuthenticated && !isAdobeIncMember) {
    return (
      <Provider
        styles={style({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        })}
      >
        <IllustratedMessage size="L" styles={style({ maxWidth: 640 })}>
          <Rocket />
          <Heading>BeStory</Heading>
          <Content
            styles={style({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              // gap: 16,
            })}
          >
            <Text>Authentication Error</Text>
            <Text>Please use the Adobe Inc. profile (@adobe.com)</Text>
          </Content>
          <ButtonGroup>
            <Button onPress={handleSwitchAccount} variant="secondary">
              Sign in with different account
            </Button>
          </ButtonGroup>
        </IllustratedMessage>
      </Provider>
    );
  }

  // Show main app if authenticated, authorized, and Adobe Inc member
  return (
    <ErrorBoundary onError={onError} FallbackComponent={fallbackComponent}>
      <App locale={locale} onLogout={handleLogout} />
    </ErrorBoundary>
  );

  // Error handling methods
  function onError(e, componentStack) {
    console.error("App error:", e, componentStack);
  }

  function fallbackComponent({ error, resetErrorBoundary }) {
    return (
      <Provider locale={locale} background="layer-2">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <h2>Something went wrong</h2>
          <p>{error.message}</p>
          <button onClick={resetErrorBoundary}>Try again</button>
        </div>
      </Provider>
    );
  }
}

export default AuthWrapper;
